import type { AssignableRole, Invitation, Role, User } from "@unbox/api";
import { Edit01, Users01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useAdminInvites } from "@/api/admin/invites";
import { useAdminUsers } from "@/api/admin/users";
import { usePagination } from "@/api/pagination";
import { LogoStack, type LogoStackItem } from "@/components/admin/logo-stack";
import { EffectiveRoleLabel, InviteRoleLabel, type ScopedRoleValue } from "@/components/admin/role-label";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table } from "@/components/application/table/table";
import { Avatar } from "@/components/base/avatar/avatar";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { useDebounce } from "@/hooks/use-debounce";
import { DeleteUserModal } from "@/pages/admin/modals/delete-user";
import { EditUserModal } from "@/pages/admin/modals/edit-user";
import { ResendInviteModal } from "@/pages/admin/modals/resend-invite";
import { RevokeInviteModal } from "@/pages/admin/modals/revoke-invite";
import { SendInviteModal } from "@/pages/admin/modals/send-invite";
import { formatDate, toDateOrNull } from "@/utils/date-conversion";
import { TabHeaderReadOnly } from "./tab-header";
import type { MerchantWithUsers } from "./types";

type AllUsersTabProps = {
  merchant: MerchantWithUsers;
};

const getUserInitials = (firstName: string | null | undefined, lastName: string | null | undefined) => {
  const initials = [firstName?.trim()?.[0], lastName?.trim()?.[0]].filter(Boolean).join("").toUpperCase();
  return initials || "U";
};

const getEmailInitials = (email: string) => {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

type UserRow = {
  type: "user";
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: Role;
  effectiveRole: ScopedRoleValue;
  createdAt: Date;
  lastActivity: Date | null;
  tosDate: Date | null;
  tosVersion: number | null;
  updatedAt: Date | null;
  deletedAt: Date | null;
  workspacesForMerchant: LogoStackItem[];
};

type InviteRow = {
  type: "invite";
  id: string;
  email: string;
  role: Role;
  assignableRole: AssignableRole;
  createdAt: string;
  expiresAt: string;
  status: "pending" | "accepted" | "revoked";
  expired: boolean;
  invite: Pick<Invitation, "id" | "status">;
};

type TableRow = UserRow | InviteRow;

function formatRelativeTime(date: Date | null): string {
  if (!date) return "—";
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

export const AllUsersTab = ({ merchant }: AllUsersTabProps) => {
  const pagination = usePagination();
  const [search] = useState("");
  const debouncedSearch = useDebounce(search);
  const [editUser, setEditUser] = useState<UserRow | undefined>(undefined);

  const { data: usersData, isLoading: usersLoading } = useAdminUsers(
    pagination.pagination,
    { column: "firstName", direction: "asc" },
    { merchantId: merchant.id, text: debouncedSearch },
  );

  const { data: invitesData, isLoading: invitesLoading } = useAdminInvites(
    { limit: 100, offset: 0 },
    { column: "createdAt", direction: "desc" },
    { merchantId: merchant.id, status: "pending" },
  );

  const isLoading = usersLoading || invitesLoading;

  const pendingInvites = useMemo(() => {
    if (!invitesData?.invitations) return [];
    return invitesData.invitations.filter((invite) => invite.status === "pending");
  }, [invitesData?.invitations]);

  const tableRows: TableRow[] = useMemo(() => {
    const activeUsers = (usersData?.users ?? []).filter((item) => !item.deletedAt);

    const users: TableRow[] = activeUsers.map((item) => {
      const rawItem = item as typeof item & {
        merchants?: { id: string; workspaceId: string | null }[];
        workspaces?: LogoStackItem[];
      };
      const workspaceIdsForMerchant = new Set(
        (rawItem.merchants ?? [])
          .filter((m) => m.id === merchant.id && m.workspaceId)
          .map((m) => m.workspaceId as string),
      );
      const workspacesForMerchant =
        workspaceIdsForMerchant.size > 0
          ? (rawItem.workspaces ?? []).filter((ws) => workspaceIdsForMerchant.has(ws.id))
          : [];

      return {
        type: "user" as const,
        id: item.id,
        email: item.email,
        firstName: item.firstName,
        lastName: item.lastName,
        role: item.role,
        effectiveRole: item.effectiveRole,
        createdAt: toDateOrNull(item.createdAt, false) as Date,
        lastActivity: toDateOrNull(item.lastActivity),
        tosDate: toDateOrNull(item.tosDate),
        tosVersion: item.tosVersion ?? null,
        updatedAt: toDateOrNull(item.updatedAt),
        deletedAt: toDateOrNull(item.deletedAt),
        workspacesForMerchant,
      };
    });

    const invites: TableRow[] = pendingInvites.map((invite) => ({
      type: "invite" as const,
      id: `invite-${invite.id}`,
      email: invite.email,
      role: invite.role,
      assignableRole: invite.assignableRole,
      createdAt: invite.createdAt,
      expiresAt: invite.expiresAt,
      status: invite.status,
      expired: new Date(invite.expiresAt) <= new Date(),
      invite,
    }));

    return [...invites, ...users];
  }, [usersData?.users, pendingInvites, merchant.id]);

  const totalPages = Math.ceil((usersData?.total ?? 0) / pagination.pagination.limit);

  return (
    <div className="flex flex-col gap-6 h-full">
      <TabHeaderReadOnly
        title={`All ${usersData?.total ?? tableRows.length} Users`}
        subtitle="This is a list of all users with access to this merchant"
        rightContent={<SendInviteModal />}
      />

      <div className="border-t border-primary" />

      <div className="flex flex-col gap-4">
        {isLoading ? (
          <p className="text-sm text-tertiary py-4">Loading users...</p>
        ) : tableRows.length > 0 ? (
          <>
            <Table aria-label="Merchant Users">
              <Table.Header>
                <Table.Head id="name" label="Name" isRowHeader className="w-[35%]" />
                <Table.Head id="role" label="Role" className="w-[15%]" />
                <Table.Head id="viaWorkspace" label="Via workspace" className="w-[18%]" />
                <Table.Head id="status" label="Status" className="w-[22%]" />
                <Table.Head id="actions" label="" className="w-[10%]" />
              </Table.Header>

              <Table.Body items={tableRows}>
                {(item) => {
                  if (item.type === "invite") {
                    return (
                      <Table.Row id={item.id}>
                        <Table.Cell className="whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <Avatar size="sm" initials={getEmailInitials(item.email)} alt={item.email} />
                            <div className="flex flex-col">
                              <p className="text-sm font-medium text-primary">{item.email}</p>
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <InviteRoleLabel assignableRole={item.assignableRole} />
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <span className="text-sm text-tertiary">—</span>
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {item.expired ? (
                              <>
                                <span className="text-sm font-medium text-warning-primary">Invite expired</span>
                                <span className="text-xs text-tertiary">{formatDate(item.createdAt)}</span>
                              </>
                            ) : (
                              <>
                                <span className="text-sm font-medium text-utility-blue-light-500">Invite sent</span>
                                <span className="text-xs text-tertiary">{formatDate(item.createdAt)}</span>
                              </>
                            )}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <ResendInviteModal invite={item.invite} />
                            <RevokeInviteModal invite={item.invite} />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  }

                  return (
                    <Table.Row id={item.id}>
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar
                            size="sm"
                            initials={getUserInitials(item.firstName, item.lastName)}
                            alt={`${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || item.email}
                          />
                          <div className="flex flex-col">
                            <p className="text-sm font-medium text-primary">
                              {item.firstName} {item.lastName}
                            </p>
                            <p className="text-sm text-tertiary">{item.email}</p>
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <EffectiveRoleLabel effectiveRole={item.effectiveRole} />
                      </Table.Cell>
                      <Table.Cell>
                        <LogoStack items={item.workspacesForMerchant} showCount={false} />
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium text-brand-tertiary_alt">Active</span>
                          <span className="text-xs text-tertiary">
                            {item.lastActivity
                              ? `Last active ${formatRelativeTime(item.lastActivity)}`
                              : formatDate(item.createdAt)}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <ButtonUtility
                            size="xs"
                            color="tertiary"
                            tooltip="Edit"
                            icon={Edit01}
                            onClick={() => setEditUser(item)}
                          />
                          <DeleteUserModal user={item} />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>

            {totalPages > 1 && (
              <PaginationPageMinimalCenter
                page={pagination.page}
                total={totalPages}
                onPageChange={pagination.setPage}
              />
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 flex-1">
            <div className="flex items-center justify-center size-14 rounded-full bg-tertiary">
              <Users01 className="size-6 text-secondary" />
            </div>
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-sm font-semibold text-primary">No users yet</p>
              <p className="text-sm text-tertiary max-w-xs">Users with access to this merchant will appear here.</p>
            </div>
          </div>
        )}
      </div>
      <EditUserModal user={editUser as User | undefined} onClose={() => setEditUser(undefined)} />
    </div>
  );
};
